import { currency } from "../../../lib/format";
import { Section } from "../view/section";
import { CurrencyInput, FormField } from "./form-field";

interface SummaryCalc {
  reinstatementEstimate: number;
  reinstatementEstimateRounded: number;
  reinstatementInflation: number;
  reinstatementInflationRounded: number;
  totalDemolition: number;
  totalDemolitionRounded: number;
  drcForFsl: number;
  drcForFslRounded: number;
  reinstatementPerUnit: number;
}

interface Props {
  summary: SummaryCalc;
  roundingAdjustments: {
    reinstatement: number;
    inflation: number;
    demolition: number;
    drc: number;
  };
  onRoundingChange: (field: string, value: number) => void;
}

function SummaryRow({
  label,
  calculated,
  rounded,
  adjustmentField,
  adjustmentValue,
  onAdjustmentChange,
}: {
  label: string;
  calculated: number;
  rounded: number;
  adjustmentField: string;
  adjustmentValue: number;
  onAdjustmentChange: (field: string, value: number) => void;
}): React.ReactElement {
  return (
    <div className="grid grid-cols-3 gap-4 items-end">
      <div>
        <p className="text-xs text-muted-foreground">{label} (calculated)</p>
        <p className="text-sm tabular-nums">{currency(calculated)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label} (rounded)</p>
        <p className="text-sm tabular-nums font-semibold">{currency(rounded)}</p>
      </div>
      <FormField label="Adjustment" htmlFor={`adj-${adjustmentField}`}>
        <CurrencyInput
          id={`adj-${adjustmentField}`}
          value={adjustmentValue}
          onChange={(v) => onAdjustmentChange(adjustmentField, v ?? 0)}
        />
      </FormField>
    </div>
  );
}

export function SectionSummaryForm({ summary, roundingAdjustments, onRoundingChange }: Props): React.ReactElement {
  return (
    <Section id="summary" title="Summary">
      <p className="text-xs text-muted-foreground mb-4">
        All values calculated automatically. Adjust rounding if needed.
      </p>
      <div className="space-y-4 max-w-3xl">
        <SummaryRow
          label="Reinstatement Cost Estimate"
          calculated={summary.reinstatementEstimate}
          rounded={summary.reinstatementEstimateRounded}
          adjustmentField="reinstatement"
          adjustmentValue={roundingAdjustments.reinstatement}
          onAdjustmentChange={onRoundingChange}
        />
        <SummaryRow
          label="Reinstatement Inflation"
          calculated={summary.reinstatementInflation}
          rounded={summary.reinstatementInflationRounded}
          adjustmentField="inflation"
          adjustmentValue={roundingAdjustments.inflation}
          onAdjustmentChange={onRoundingChange}
        />
        <SummaryRow
          label="Demolition"
          calculated={summary.totalDemolition}
          rounded={summary.totalDemolitionRounded}
          adjustmentField="demolition"
          adjustmentValue={roundingAdjustments.demolition}
          onAdjustmentChange={onRoundingChange}
        />
        <SummaryRow
          label="DRC for Fire and Emergency Levy"
          calculated={summary.drcForFsl}
          rounded={summary.drcForFslRounded}
          adjustmentField="drc"
          adjustmentValue={roundingAdjustments.drc}
          onAdjustmentChange={onRoundingChange}
        />
        <div className="border-t pt-4">
          <p className="text-xs text-muted-foreground">Reinstatement Cost Estimate per Unit</p>
          <p className="text-lg font-semibold tabular-nums">{currency(summary.reinstatementPerUnit)}</p>
        </div>
      </div>
    </Section>
  );
}
